/**
 * Firebase Auth module for jsond.ca
 *
 * Uses Firebase compat SDK loaded from CDN in index.html.
 * Provides Svelte stores for auth state and helper functions.
 */

import { writable, derived } from 'svelte/store';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const DEV_MODE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = 'https://email-loop-api-12364253149.us-central1.run.app';

const FIREBASE_CONFIG = {
  apiKey: 'AIzaSyD6xJqyMEFAWDXjBug5H4CI0qZQF2L5Rno',
  authDomain: 'edai-sheets-467504.firebaseapp.com',
  projectId: 'edai-sheets-467504'
};

// ---------------------------------------------------------------------------
// Stores
// ---------------------------------------------------------------------------

export const authState = writable({ user: null, tenant: null, loading: true });
export const isAuthenticated = derived(authState, $s => !!$s.user);

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

let firebaseAuth = null;
let initialized = false;
const SESSION_MAX_MS = 2 * 60 * 60 * 1000; // 2 hours
let sessionTimer = null;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function initAuth() {
  if (initialized) return;
  initialized = true;

  // Dev mode: bypass Firebase, use mock user
  if (DEV_MODE) {
    authState.set({
      user: { email: 'dev@localhost' },
      tenant: { id: 'dev-tenant', name: 'Dev Workspace' },
      loading: false
    });
    return;
  }

  // Initialize Firebase (compat SDK from CDN)
  if (!window.firebase.apps.length) {
    window.firebase.initializeApp(FIREBASE_CONFIG);
  }
  firebaseAuth = window.firebase.auth();

  // Listen for auth state changes
  firebaseAuth.onAuthStateChanged(async (firebaseUser) => {
    clearTimeout(sessionTimer);
    if (firebaseUser) {
      // Check if token is older than 2 hours
      const result = await firebaseUser.getIdTokenResult();
      const authTimeMs = new Date(result.authTime).getTime();
      const elapsed = Date.now() - authTimeMs;
      if (elapsed > SESSION_MAX_MS) {
        console.log('Session expired (>2h), signing out');
        await firebaseAuth.signOut();
        return;
      }

      // Schedule auto-logout for remaining time
      const remaining = SESSION_MAX_MS - elapsed;
      sessionTimer = setTimeout(async () => {
        console.log('Session expired, signing out');
        await firebaseAuth.signOut();
      }, remaining);

      // Show App-level loading spinner while backend validates
      authState.update(s => ({ ...s, loading: true }));
      try {
        const token = await firebaseUser.getIdToken();
        const res = await fetch(API_BASE + '/api/auth/login', {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' }
        });
        if (!res.ok) throw new Error('Login failed (' + res.status + ')');
        const data = await res.json();
        authState.set({ user: data.user, tenant: data.tenant, loading: false });
      } catch (err) {
        console.error('Auth login failed:', err);
        authState.set({ user: null, tenant: null, loading: false });
      }
    } else {
      authState.set({ user: null, tenant: null, loading: false });
    }
  });
}

export async function signInWithGoogle() {
  const provider = new window.firebase.auth.GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  await firebaseAuth.signInWithPopup(provider);
  // onAuthStateChanged will fire and handle the rest
}

export async function signOut() {
  await firebaseAuth.signOut();
}

export async function getAuthToken() {
  if (DEV_MODE) return 'dev-token';
  const user = firebaseAuth && firebaseAuth.currentUser;
  return user ? user.getIdToken() : null;
}

// ---------------------------------------------------------------------------
// Magic link sign-in (P4.c — passwordless email)
// ---------------------------------------------------------------------------

const MAGIC_LINK_EMAIL_KEY = 'adrasi_magic_link_email';

/**
 * Send a magic sign-in link to the given email. The link sends the user
 * back to /sign-in/complete (preserving any URL query params on the
 * current page, e.g. mcp_state for the MCP OAuth handoff).
 */
export async function sendMagicLink(email) {
  // Preserve current URL query params (mcp_state, mcp_complete_url) so
  // the user lands back in the right flow after clicking the email link.
  const continueUrl = new URL(window.location.origin + '/sign-in/complete');
  for (const [key, value] of new URLSearchParams(window.location.search)) {
    continueUrl.searchParams.set(key, value);
  }

  const actionCodeSettings = {
    url: continueUrl.toString(),
    handleCodeInApp: true,
  };

  await firebaseAuth.sendSignInLinkToEmail(email, actionCodeSettings);
  // Persist the email for cross-device flows (user clicks link on a
  // different device than they entered it on — Firebase requires the
  // email to verify the link).
  window.localStorage.setItem(MAGIC_LINK_EMAIL_KEY, email);
}

/**
 * Check whether the current URL is a magic sign-in link from Firebase.
 */
export function isSignInWithEmailLink(href) {
  if (DEV_MODE) return false;
  if (!firebaseAuth) return false;
  return firebaseAuth.isSignInWithEmailLink(href);
}

/**
 * Complete a magic-link sign-in. Called when the user clicks the link
 * in their email and lands at /sign-in/complete.
 *
 * If the email isn't in localStorage (cross-device click), pass the
 * `confirmedEmail` argument after prompting the user.
 */
export async function completeMagicLink(href, confirmedEmail) {
  let email = confirmedEmail || window.localStorage.getItem(MAGIC_LINK_EMAIL_KEY);
  if (!email) {
    const e = new Error('Email needed to complete sign-in');
    e.code = 'email-needed';
    throw e;
  }
  await firebaseAuth.signInWithEmailLink(email, href);
  window.localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
  // onAuthStateChanged will fire and handle the rest
}
